import { ButtonLink } from '@/components/Button'
import CloudinaryImage from '@/components/CloudinaryImage'
import SimpleFluidContainer from '@/components/FluidContainer/SimpleFluidContainer'
import HeroImage from '@/components/HeroImage'
import { useWindowWidth } from '@/contexts/windowDimensions'
import useBreakpoints from '@/hooks/useBreakpoints'
import { classNames } from '@/util/tailwindHelpers'
import { HOW_TO_PLAY_PATH, LEADERBOARD_PATH } from '../hooks/routing'

interface Props {
  className?: string
}

const imgPath = 'pages/arcade/'

const ArcadeBlade = ({ className }: Props) => {
  const { isLarge } = useBreakpoints()
  const windowWidth = useWindowWidth()
  const bladeBg = isLarge
    ? 'arcade'
    : windowWidth > 593 && windowWidth < 925
    ? 'arcade-tablet'
    : 'arcade-mobile'

  const heroImageProps = {
    path: imgPath,
    id: bladeBg,
    alt: 'Droppp Arcade',
    sizing: { height: { mini: 364, full: 1276 }, ratio: 2.69, maxWidth: 1800 },
    // Custom Options: to container, image offset & sizing
    doubleRatio: true,
    className: '-mt-8 lg:mt-0',
    offset: `translate-x-[-10%] min-[420px]:translate-x-[-5%] lg:translate-x-[20%]`
  }

  return isLarge ? (
    <div className={classNames('overflow-hidden', className)}>
      <section className="lg:container relative">
        <SimpleFluidContainer
          targetWidth={1200}
          className={`inset-0 mx-4 flex flex-col items-center text-center lg:absolute lg:justify-center lg:text-left lg:items-start`}
        >
          <div className="min-h-[450px] lg:min-h-max mt-[38px] lg:mt-0">
            <div
              className="max-sm:!w-full max-sm:max-w-lg"
              style={{
                width: isLarge ? 'calc(1em * 70)' : 'auto'
              }}
            >
              <div className="utility-lg">COLLECT. CONVERT. COMPETE.</div>
              <h2 className="h2 lg:h1 my-2 !leading-none lg:text-[7em]">
                <span>Dropppp</span>{' '}
                <span className="text-arcadeGreen">Arcade</span>
              </h2>
              <p className="mb-4 body-[20px] body lg:text-[2.25em]">
                Convert eligible digital collectibles into points to rank on the
                Arcade Leaderboard. The top 333 ranks will receive a
                highly-limited Pop! vinyl figure!
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-2 items-center">
              <ButtonLink
                theme="green-arcade"
                size="lg"
                className="w-max"
                href={LEADERBOARD_PATH}
              >
                View Leaderboard
              </ButtonLink>
              <ButtonLink
                theme="secondary"
                size="lg"
                className="w-max"
                href={HOW_TO_PLAY_PATH}
              >
                How It Works
              </ButtonLink>
            </div>
          </div>
        </SimpleFluidContainer>
        <HeroImage {...heroImageProps} />
      </section>
    </div>
  ) : (
    <section
      className={classNames('relative overflow-hidden ', {
        'h-[839px]': windowWidth <= 593,
        'aspect-[900/839] ': windowWidth > 593 && windowWidth < 925
      })}
    >
      <div
        style={{
          background:
            'linear-gradient(158.52deg, rgba(9, 9, 9, 0.20) 37.58%, rgba(9, 9, 9, 0.33) 59.23%, rgba(9, 9, 9, 0) 88.49%)'
        }}
        className={`h-[100%] absolute top-0 left-0 right-0 mix-blend-hard-light`}
      ></div>
      <CloudinaryImage
        path={imgPath}
        imageId={bladeBg}
        layout="fill"
        objectFit="cover"
        objectPosition={
          windowWidth > 509 && windowWidth <= 593 ? 'center -200px' : 'center'
        }
        alt="Arcade Background"
        className="-z-10"
        transformations={{
          quality: 75,
          resize: { type: 'scale', width: 2500 }
        }}
      />
      <div className="container mx-auto relative">
        <SimpleFluidContainer
          targetWidth={1200}
          className="relative flex flex-col items-center justify-center text-center py-8"
        >
          <>
            <div
              className="max-sm:!w-full max-sm:max-w-lg"
              style={{
                width: 'auto'
              }}
            >
              <div className="utility-lg">COLLECT. CONVERT. COMPETE.</div>
              <h2 className="h2 my-2 !leading-none">
                <span>Dropppp</span>{' '}
                <span className="text-arcadeGreen">Arcade</span>
              </h2>
              <p className="mb-4 body-[20px] body">
                Convert eligible digital collectibles into points to rank on the
                Arcade Leaderboard. The top 333 ranks will receive a
                highly-limited Pop! vinyl figure!
              </p>
            </div>
            <div className="flex flex-col gap-2 items-center">
              <ButtonLink
                theme="green-arcade"
                size="lg"
                className="w-max"
                href={LEADERBOARD_PATH}
              >
                View Leaderboard
              </ButtonLink>
              <ButtonLink
                theme="secondary"
                size="lg"
                className="w-max"
                href={HOW_TO_PLAY_PATH}
              >
                How It Works
              </ButtonLink>
            </div>
          </>
        </SimpleFluidContainer>
      </div>
    </section>
  )
}

export default ArcadeBlade
